import { memo } from 'react';
import { toaster } from '@optiaxiom/react';
import { IconEnvelope } from '@optimizely/axiom-icons';
import { NodeProps } from '@xyflow/react';

import { extractApiErrorMessage } from '@frontend/api/http';

import {
  createEmailTrigger,
  deleteEmailTrigger,
  getEmailTrigger,
  updateEmailTrigger,
} from '../../services/email-trigger';
import { getTriggerSelfUrl } from '../../types/api';
import { NodeConnectionTypes, NodeTypeCategory } from '../../types/connections';
import { NODE_TYPE } from '../../types/node';
import { defineNode } from '../../utils/node-factory';
import { BaseNodeRenderer } from '../components/renderers/BaseNodeRenderer/BaseNodeRenderer';

/**
 * Configuration for the Email Trigger node type.
 * Triggers workflow execution from incoming emails.
 */
export const emailTriggerConfig = defineNode({
  description: {
    displayName: 'Email Trigger',
    name: NODE_TYPE.EMAIL,
    description: 'Trigger workflow execution from incoming emails',
    iconInfo: {
      icon: IconEnvelope,
      color: 'fg.success',
    },
    category: NodeTypeCategory.TRIGGER,
    input: {
      type: NodeConnectionTypes.Main,
      filter: undefined,
    },
    output: {
      type: NodeConnectionTypes.Main,
      displayName: 'Output',
      maxConnections: 1,
    },
    properties: [
      {
        displayName: 'Name',
        name: 'name',
        type: 'string',
        required: true,
        default: '',
        description: 'A descriptive name for this email trigger',
      },
      {
        displayName: 'Description',
        name: 'description',
        type: 'string',
        required: false,
        default: '',
        description: 'Optional description of what emails trigger this workflow',
      },
      {
        displayName: 'Recipient',
        name: 'recipient',
        type: 'string',
        required: true,
        default: '',
        description:
          'Recipient identifier for the email address (kebab-case, e.g., "support-tickets")',
      },
      {
        displayName: 'Email Address',
        name: 'email_address',
        type: 'string',
        required: false,
        default: '',
        description: 'The actual email address for this trigger (display only)',
        readonly: true,
      },
      {
        displayName: 'Subject Filters',
        name: 'subject_filters',
        type: 'filters',
        required: false,
        default: [],
        description: 'Filter emails by subject line. Emails matching any filter will be accepted.',
      },
      {
        displayName: 'Sender Filters',
        name: 'sender_filters',
        type: 'filters',
        required: true,
        default: [],
        description:
          'Filter emails by sender address. Emails matching any filter will be accepted. At least one sender filter is required.',
      },
    ],
    defaults: {
      name: 'Email Trigger',
      parameters: {
        name: '',
        description: '',
        recipient: '',
        email_address: '',
        subject_filters: [],
        sender_filters: [],
      },
    },
  },

  enableAsyncGet: (_nodeId: string, nodeData?: Record<string, unknown>) => {
    const selfUrl = getTriggerSelfUrl(nodeData);
    return Boolean(selfUrl && selfUrl.trim().length > 0);
  },

  onAsyncSave: async (
    _nodeId: string,
    data: Record<string, unknown>,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    const selfUrl = getTriggerSelfUrl(context.nodeData);
    if (!selfUrl) {
      try {
        const emailTrigger = await createEmailTrigger(
          context.workflowAgentId,
          context.instanceId,
          data
        );
        return emailTrigger;
      } catch (error) {
        const errorMessage = extractApiErrorMessage(error);
        throw new Error(errorMessage);
      }
    }

    try {
      await updateEmailTrigger(selfUrl, data);
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error);
      throw new Error(errorMessage);
    }
  },

  onAsyncGet: async (
    _nodeId: string,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    const selfUrl = getTriggerSelfUrl(context.nodeData);
    if (!selfUrl) {
      throw new Error('URL not found in node data');
    }

    try {
      const emailTrigger = await getEmailTrigger(selfUrl);
      return emailTrigger;
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        return {};
      }

      console.error('Failed to fetch email trigger:', error);
      toaster.create('Failed to load email trigger configuration', { intent: 'danger' });
      throw error;
    }
  },

  onAsyncDelete: async (
    _nodeId: string,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    const selfUrl = getTriggerSelfUrl(context.nodeData);
    if (!selfUrl) {
      throw new Error('URL not found in node data');
    }

    try {
      await deleteEmailTrigger(selfUrl);
    } catch (error) {
      console.error('Failed to delete email trigger:', error);
      const errorMessage = extractApiErrorMessage(error);
      throw new Error(errorMessage);
    }
  },

  render: memo(function EmailTriggerRenderer(props: NodeProps) {
    return <BaseNodeRenderer {...props} />;
  }),
});
